import React, { useEffect, useState } from "react";
import { Spin } from "antd";
import { ShieldCheck, ShieldOff } from "lucide-react";
import { mfaApi, MfaStatusResponse } from "../../api/mfa";
import { getUserFromToken } from "../../utils/utils";
import MfaSetupModal from "./components/MfaSetupModal";
import MfaDisableModal from "./components/MfaDisableModal";
import RegenerateBackupCodesModal from "./components/RegenerateBackupCodesModal";

const Security: React.FC = () => {
  const userId = getUserFromToken() || "";
  const [status, setStatus] = useState<MfaStatusResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [setupOpen, setSetupOpen] = useState(false);
  const [disableOpen, setDisableOpen] = useState(false);
  const [regenerateOpen, setRegenerateOpen] = useState(false);

  const fetchStatus = () => {
    if (!userId) return;
    setLoading(true);
    mfaApi
      .getStatus(userId)
      .then((res) => setStatus(res.data))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  return (
    <div className="p-8 flex flex-col">
      <div className="flex justify-between">
        <h1 className="text-xl font-semibold text-gray-900">Security</h1>
      </div>
      <hr className="my-4 border-gray-200" />

      <div className="bg-white rounded-lg p-6 border border-gray-200 shadow-sm max-w-2xl">
        <h2 className="text-base font-semibold text-gray-900 mb-1">
          Two-factor authentication
        </h2>
        <p className="text-sm text-gray-600 mb-4">
          Add an extra layer of security to your account by requiring a code
          from an authenticator app when you log in.
        </p>

        {loading ? (
          <div className="py-4">
            <Spin />
          </div>
        ) : status?.mfaEnabled ? (
          <div className="space-y-4">
            <div className="flex items-center gap-2 text-sm text-green-700">
              <ShieldCheck className="w-5 h-5" />
              Enabled
              {status.enrolledAt
                ? ` since ${new Date(status.enrolledAt).toLocaleDateString()}`
                : ""}
            </div>
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => setRegenerateOpen(true)}
                className="px-4 py-2 rounded-lg font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 transition-colors"
              >
                Regenerate backup codes
              </button>
              <button
                type="button"
                onClick={() => setDisableOpen(true)}
                className="px-4 py-2 rounded-lg font-medium text-gray-700 border-2 border-gray-300 hover:bg-gray-50 transition-colors"
              >
                Disable
              </button>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center gap-2 text-sm text-gray-500">
              <ShieldOff className="w-5 h-5" />
              Two-factor authentication is off
            </div>
            <button
              type="button"
              onClick={() => setSetupOpen(true)}
              className="px-4 py-2 rounded-lg font-medium bg-primary hover:bg-primary-dark text-white transition-colors"
            >
              Enable
            </button>
          </div>
        )}
      </div>

      <MfaSetupModal
        open={setupOpen}
        userId={userId}
        onClose={() => setSetupOpen(false)}
        onEnabled={fetchStatus}
      />
      <MfaDisableModal
        open={disableOpen}
        userId={userId}
        onClose={() => setDisableOpen(false)}
        onDisabled={fetchStatus}
      />
      <RegenerateBackupCodesModal
        open={regenerateOpen}
        userId={userId}
        onClose={() => setRegenerateOpen(false)}
      />
    </div>
  );
};

export default Security;
